<template>
  <el-container class="workspace-page issue-stats-page">
    <el-main class="workspace-content">
      <div class="stats-toolbar">
        <el-date-picker
            v-model="dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            :range-separator="$t('至')"
            :start-placeholder="$t('开始日期')"
            :end-placeholder="$t('结束日期')"
            popper-class="responsive-date-picker"
            @change="onDateRangeChange"
        />
        <el-button type="primary" :loading="loading" @click="fetchStats">{{ $t("刷新") }}</el-button>
      </div>
      <div class="stats-panel">
        <div class="stats-summary">
          <span>{{ $t("统计周期：{p0}", { p0: dateRangeText }) }}</span>
          <span class="stats-total">{{ $t("总记录：{p0}", { p0: totalCount }) }}</span>
        </div>
        <div v-if="loading" class="stats-loading">{{ $t("加载中...") }}</div>
        <div v-else-if="!chartSeries.length" class="stats-empty">{{ $t("暂无数据") }}</div>
        <div v-else class="stats-chart">
          <svg :viewBox="chartViewBox" class="stats-chart-svg" role="img" :aria-label="$t('问题统计图')">
          <line
              :x1="padding.left"
              :x2="chartWidth - padding.right"
              :y1="chartHeight - padding.bottom"
              :y2="chartHeight - padding.bottom"
              class="axis-line"
          />
          <line
              :x1="padding.left"
              :x2="padding.left"
              :y1="padding.top"
              :y2="chartHeight - padding.bottom"
              class="axis-line"
          />
          <text
              :x="padding.left - 8"
              :y="padding.top + 4"
              text-anchor="end"
              class="axis-label"
          >{{ maxCount }}</text>
          <text
              :x="padding.left - 8"
              :y="chartHeight - padding.bottom + 4"
              text-anchor="end"
              class="axis-label"
          >0</text>
          <polyline
              :points="linePoints"
              class="line-path"
              fill="none"
          />
          <circle
              v-for="point in lineDots"
              :key="`dot-${point.index}`"
              :cx="point.x"
              :cy="point.y"
              r="3"
              class="line-dot"
          />
          </svg>
          <div class="stats-x-axis">
            <span
                v-for="label in axisLabels"
                :key="label.date"
                class="stats-x-label"
                :style="{ left: label.left }"
            >{{ label.date }}</span>
          </div>
        </div>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import { t } from "@/i18n";
import axios from 'axios';
import app from '@/App';

export default {
  name: 'IssueStats',
  data() {
    return {
      user: {},
      loading: false,
      dateRange: null,
      stats: [],
      chartWidth: 1000,
      chartHeight: 320,
      padding: {
        top: 20,
        right: 20,
        bottom: 30,
        left: 40,
      },
    };
  },
  computed: {
    chartViewBox() {
      return `0 0 ${this.chartWidth} ${this.chartHeight}`;
    },
    dateRangeText() {
      if (!Array.isArray(this.dateRange) || this.dateRange.length !== 2) return '—';
      return t("{p0} 至 {p1}", { p0: this.dateRange[0], p1: this.dateRange[1] });
    },
    chartSeries() {
      if (!Array.isArray(this.stats)) return [];
      const range = this.dateRange;
      if (!Array.isArray(range) || range.length !== 2) return [];
      const map = new Map();
      this.stats.forEach((item) => {
        if (item && item.date) {
          map.set(item.date, Number(item.count) || 0);
        }
      });
      const [startDate, endDate] = range;
      const series = [];
      let cursor = new Date(startDate);
      const end = new Date(endDate);
      while (cursor <= end) {
        const dateStr = this.formatDate(cursor);
        series.push({ date: dateStr, count: map.get(dateStr) || 0 });
        cursor = this.addDays(cursor, 1);
      }
      return series;
    },
    maxCount() {
      return Math.max(1, ...this.chartSeries.map(item => item.count));
    },
    totalCount() {
      return this.chartSeries.reduce((sum, item) => sum + item.count, 0);
    },
    linePoints() {
      const n = this.chartSeries.length || 1;
      const plotWidth = this.chartWidth - this.padding.left - this.padding.right;
      const plotHeight = this.chartHeight - this.padding.top - this.padding.bottom;
      const barStep = plotWidth / n;
      return this.chartSeries.map((item, index) => {
        const x = this.padding.left + index * barStep + barStep / 2;
        const y = this.padding.top + (plotHeight - (item.count / this.maxCount) * plotHeight);
        return `${x},${y}`;
      }).join(' ');
    },
    lineDots() {
      const n = this.chartSeries.length || 1;
      const plotWidth = this.chartWidth - this.padding.left - this.padding.right;
      const plotHeight = this.chartHeight - this.padding.top - this.padding.bottom;
      const barStep = plotWidth / n;
      return this.chartSeries.map((item, index) => {
        const x = this.padding.left + index * barStep + barStep / 2;
        const y = this.padding.top + (plotHeight - (item.count / this.maxCount) * plotHeight);
        return { index, x, y };
      });
    },
    axisLabels() {
      const n = this.chartSeries.length || 1;
      const plotWidth = this.chartWidth - this.padding.left - this.padding.right;
      const barStep = plotWidth / n;
      const step = Math.ceil(n / 8);
      return this.chartSeries
        .map((item, index) => ({ item, index }))
        .filter(({ index }) => index % step === 0 || index === n - 1)
        .map(({ item, index }) => {
          const left = `${((this.padding.left + index * barStep + barStep / 2) / this.chartWidth) * 100}%`;
          return { date: item.date, left };
        });
    },
  },
  created() {
    app.methods.getUser().then((res) => {
      this.user = res.data;
      const end = new Date();
      const start = this.addDays(new Date(), -29);
      this.dateRange = [this.formatDate(start), this.formatDate(end)];
      this.fetchStats();
    });
  },
  methods: {
    addDays(date, days) {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
    },
    formatDate(value) {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    onDateRangeChange() {
      this.fetchStats();
    },
    fetchStats() {
      if (this.loading) return;
      this.loading = true;
      const [startDate, endDate] = Array.isArray(this.dateRange) ? this.dateRange : [];
      axios
        .get(process.env.VUE_APP_BASE_URL + '/issues/daily-counts', {
          params: {
            startDate,
            endDate,
          },
        })
        .then((res) => {
          this.stats = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.$message.error(t("获取问题统计失败"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.stats-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.stats-panel {
  padding: 22px;
  border: 1px solid var(--border-light);
  border-radius: 16px;
  background: var(--surface-color);
  box-shadow: 0 4px 18px var(--shadow-color);
}

.stats-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  color: var(--text-regular);
  margin-bottom: 16px;
}

.stats-total {
  font-weight: 600;
  color: var(--text-primary);
}

.stats-loading,
.stats-empty {
  text-align: center;
  color: #909399;
  padding: 24px 0;
}

.stats-chart {
  position: relative;
  width: 100%;
}

.stats-chart-svg {
  width: 100%;
  height: 320px;
  display: block;
}

.axis-line {
  stroke: #dcdfe6;
  stroke-width: 1;
}

.axis-label {
  fill: #909399;
  font-size: 12px;
}

.line-path {
  stroke: var(--primary-color);
  stroke-width: 2;
}

.line-dot {
  fill: var(--primary-color);
}

@media (max-width: 600px) {
  .stats-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .stats-toolbar .el-date-editor,
  .stats-toolbar .el-button {
    width: 100%;
  }

  .stats-panel {
    overflow-x: auto;
    padding: 16px;
  }

  .stats-summary {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .stats-chart {
    min-width: 620px;
  }
}

.stats-x-axis {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 20px;
}

.stats-x-label {
  position: absolute;
  transform: translateX(-50%);
  font-size: 11px;
  color: #909399;
}
</style>
